import { useState, useEffect } from 'react';
import { expenseService, staffService, payoutService, Expense, Staff, Payout } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  Search,
  Wallet,
  IndianRupee,
  Loader2,
  Pencil,
  Trash2,
  Calendar,
  Banknote,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const getStatusBadge = (expense: Expense) => {
  if (expense.status === 'pending') {
    return { label: 'Pending', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
  }
  if (expense.status === 'rejected') {
    return { label: 'Rejected', className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' };
  }
  
  const paid = expense.paidAmount || 0;
  if (paid === 0) {
    return {
      label: 'Approved',
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-450'
    };
  }
  if (paid < expense.amount) {
    return {
      label: 'Partially Paid',
      className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 font-bold'
    };
  }
  return {
    label: 'Fully Paid',
    className: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 font-bold'
  };
};

export default function AdminExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [staffFilter, setStaffFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [adminRemark, setAdminRemark] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editRemark, setEditRemark] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [expenseData, staffData, payoutData] = await Promise.all([
        expenseService.getAll(),
        staffService.getAll(),
        payoutService.getAll(),
      ]);
      setExpenses(expenseData);
      setStaffList(staffData);
      setPayouts(payoutData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 10000);
    return () => clearInterval(interval);
  }, []);

  const openReview = (expense: Expense) => {
    setSelectedExpense(expense);
    setEditAmount(String(expense.amount));
    setEditRemark(expense.remark || '');
    setAdminRemark('');
  };

  const filteredExpenses = expenses.filter(e => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (staffFilter !== 'all' && e.staffId !== staffFilter) return false;
    if (startDate && e.date < startDate) return false;
    if (endDate && e.date > endDate) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const staff = staffList.find(s => s.id === e.staffId);
      if (!staff?.name.toLowerCase().includes(q) && !e.remark?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleSave = async (id: string) => {
    setProcessing(true);
    try {
      const newAmount = parseFloat(editAmount);
      if (isNaN(newAmount) || newAmount <= 0) return;
      await expenseService.update(id, { amount: newAmount, remark: editRemark || undefined });
      closeReview();
      await fetchData();
    } catch (error) {
      console.error('Error saving expense:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this expense permanently?')) return;
    setProcessing(true);
    try {
      await expenseService.delete(id);
      await fetchData();
    } catch (error) {
      console.error('Error deleting expense:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveReject = async (id: string, status: 'approved' | 'rejected') => {
    setProcessing(true);
    try {
      const newAmount = parseFloat(editAmount);
      if (!isNaN(newAmount) && newAmount > 0) {
        await expenseService.update(id, { amount: newAmount, remark: editRemark || undefined });
      }
      await expenseService.updateStatus(id, status, adminRemark || undefined);
      closeReview();
      await fetchData();
    } catch (error) {
      console.error('Error updating expense:', error);
    } finally {
      setProcessing(false);
    }
  };

  const closeReview = () => {
    setSelectedExpense(null);
    setAdminRemark('');
    setEditAmount('');
    setEditRemark('');
  };

  const getStaffName = (staffId: string) => {
    return staffList.find(s => s.id === staffId)?.name || 'Unknown';
  };

  const statsExpenses = expenses.filter(e => {
    if (staffFilter !== 'all' && e.staffId !== staffFilter) return false;
    if (startDate && e.date < startDate) return false;
    if (endDate && e.date > endDate) return false;
    return true;
  });

  const pendingCount = statsExpenses.filter(e => e.status === 'pending').length;
  const approvedTotal = statsExpenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0);
  const pendingTotal = statsExpenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.amount, 0);

  // Filter payouts by staff and date range
  const statsPayouts = payouts.filter(p => {
    if (staffFilter !== 'all' && p.staffId !== staffFilter) return false;
    const pDate = p.createdAt.split('T')[0];
    if (startDate && pDate < startDate) return false;
    if (endDate && pDate > endDate) return false;
    return true;
  });
  const totalPayoutsPaid = statsPayouts.reduce((sum, p) => sum + p.amount, 0);

  // Sum current remaining wallet balance for active staff filters
  const statsStaff = staffList.filter(s => {
    if (staffFilter !== 'all' && s.id !== staffFilter) return false;
    return true;
  });
  const totalRemainingWallet = statsStaff.reduce((sum, s) => sum + (s.walletBalance || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Expenses & Payroll</h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
            Review and approve staff expense requests and monitor processed payouts
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <Card className="border-slate-200/60 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
          <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-bold flex items-center gap-1.5 sm:gap-2 text-slate-700 dark:text-slate-350 truncate">
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-500 shrink-0" />
              Pending Expenses
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="text-lg sm:text-2xl font-black text-slate-800 dark:text-slate-200 truncate">
              ₹{pendingTotal.toLocaleString('en-IN')}
            </div>
            <p className="text-[10px] sm:text-xs font-semibold text-slate-500 mt-0.5 truncate">
              {pendingCount} request{pendingCount !== 1 ? 's' : ''} pending
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200/60 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
          <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-bold flex items-center gap-1.5 sm:gap-2 text-slate-700 dark:text-slate-350 truncate">
              <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 shrink-0" />
              Approved Expenses
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="text-lg sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 truncate">
              ₹{approvedTotal.toLocaleString('en-IN')}
            </div>
            <p className="text-[10px] sm:text-xs font-semibold text-slate-500 mt-0.5 truncate">
              Total approved claims
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200/60 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
          <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-bold flex items-center gap-1.5 sm:gap-2 text-slate-700 dark:text-slate-350 truncate">
              <Banknote className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-500 shrink-0" />
              Paid (via Payouts)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="text-lg sm:text-2xl font-black text-indigo-600 dark:text-indigo-400 truncate">
              ₹{totalPayoutsPaid.toLocaleString('en-IN')}
            </div>
            <p className="text-[10px] sm:text-xs font-semibold text-slate-500 mt-0.5 truncate">
              UPI payout transactions
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200/60 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
          <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
            <CardTitle className="text-xs sm:text-sm font-bold flex items-center gap-1.5 sm:gap-2 text-slate-700 dark:text-slate-350 truncate">
              <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-rose-500 shrink-0" />
              Remaining (Wallet)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="text-lg sm:text-2xl font-black text-rose-600 dark:text-rose-400 truncate">
              ₹{totalRemainingWallet.toLocaleString('en-IN')}
            </div>
            <p className="text-[10px] sm:text-xs font-semibold text-slate-500 mt-0.5 truncate">
              Unpaid wages balance
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3 w-full">
        <div className="relative flex-grow max-w-md w-full sm:w-auto">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by staff or remark..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 rounded-xl bg-white/50 dark:bg-slate-955/40 border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-700 transition-all font-medium text-sm w-full"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <Filter className="w-3.5 h-3.5 mr-1" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={staffFilter} onValueChange={setStaffFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="All Staff" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Staff</SelectItem>
            {staffList.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Custom Date Range Picker */}
        <div className="flex flex-wrap items-center gap-2 bg-white/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 p-2 rounded-xl h-auto min-h-[44px] w-full sm:w-auto">
          <span className="text-xs font-bold text-slate-505 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-indigo-500" />
            From:
          </span>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-8 text-xs rounded-lg border-slate-200 dark:border-slate-855 bg-white dark:bg-slate-900 w-[125px]"
          />
          <span className="text-xs font-bold text-slate-505">To:</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-8 text-xs rounded-lg border-slate-200 dark:border-slate-855 bg-white dark:bg-slate-900 w-[125px]"
          />
          {(startDate || endDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setStartDate(''); setEndDate(''); }}
              className="h-8 px-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold text-rose-500 hover:text-rose-600"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Expenses Table */}
      <Card className="border-slate-200/60 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Wallet className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No expenses found</p>
              <p className="text-xs mt-1">Staff expense submissions will appear here.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Remark</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell className="font-medium">
                      {getStaffName(expense.staffId)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(expense.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className={cn(
                          'inline-flex items-center w-fit px-2 py-0.5 rounded-full text-xs font-medium',
                          expense.category === 'Travel Allowance'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                        )}>
                          {expense.category || 'Other Exp'}
                        </span>
                        {expense.clientName && (
                          <span className="text-[10px] text-amber-600 dark:text-amber-400 font-bold">
                            Client: {expense.clientName}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-rose-600 dark:text-rose-400">
                      -₹{expense.amount.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {expense.remark || '—'}
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        getStatusBadge(expense).className
                      )}>
                        {getStatusBadge(expense).label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {expense.status === 'pending' ? (
                          <Button
                            size="sm"
                            variant="success"
                            className="h-8 px-3"
                            onClick={() => openReview(expense)}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            Review
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-3"
                            onClick={() => openReview(expense)}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400"
                          onClick={() => handleDelete(expense.id)}
                          disabled={processing}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!selectedExpense} onOpenChange={(v) => { if (!v) closeReview(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Review & Edit Expense
            </DialogTitle>
            <DialogDescription>
              {selectedExpense && (
                <span>
                  {getStaffName(selectedExpense.staffId)} · {new Date(selectedExpense.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedExpense && (
            <div className="space-y-4">
              {selectedExpense.clientName && (
                <div className="space-y-1">
                  <Label className="text-[10px] font-black uppercase tracking-wider text-slate-400">Client Name</Label>
                  <div className="text-xs font-bold text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3 py-2 rounded-xl">
                    {selectedExpense.clientName}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium">Amount (₹)</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Staff Remark</Label>
                <Textarea
                  value={editRemark}
                  onChange={(e) => setEditRemark(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Admin Remark (optional)</Label>
                <Textarea
                  placeholder="Add a note..."
                  value={adminRemark}
                  onChange={(e) => setAdminRemark(e.target.value)}
                  rows={2}
                />
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  variant="outline"
                  onClick={closeReview}
                  disabled={processing}
                >
                  Cancel
                </Button>
                {selectedExpense.status === 'pending' ? (
                  <>
                    <Button
                      variant="destructive"
                      className="gap-1.5"
                      onClick={() => handleApproveReject(selectedExpense.id, 'rejected')}
                      disabled={processing}
                    >
                      {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                      Reject
                    </Button>
                    <Button
                      variant="success"
                      className="gap-1.5"
                      onClick={() => handleApproveReject(selectedExpense.id, 'approved')}
                      disabled={processing}
                    >
                      {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Approve
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="royal"
                    className="gap-1.5"
                    onClick={() => handleSave(selectedExpense.id)}
                    disabled={processing}
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Save Changes
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
